import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Download, CheckCircle, AlertCircle, SkipForward, AlertTriangle, Repeat } from 'lucide-react';
import { auth } from '../../firebase';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';

// Two import modes share this modal:
//   1. Basic lead import  — first 9 columns only (prospects / pipeline).
//   2. Migration import   — adds program_name + next_service_date + optional
//      assigned_tech + price_override. Backend matches program by name and
//      activates the existing recurring engine. Unmatched program names FAIL
//      the row by design (see import_batch_routes.py — strict policy).
const TEMPLATE_CSV_LEADS = [
  'first_name,last_name,phone,email,address,city,state,zip,stage',
  'John,Smith,5551234567,john@example.com,123 Main St,Austin,TX,78701,New Lead',
  'Mary,Jones,5559876543,mary@example.com,456 Oak Ave,Dallas,TX,75201,In Progress',
].join('\n');

const TEMPLATE_CSV_MIGRATION = [
  'first_name,last_name,phone,email,address,city,state,zip,stage,program_name,next_service_date,assigned_tech,price_override',
  'John,Smith,5551234567,john@example.com,123 Main St,Austin,TX,78701,,Quarterly Pest,2026-09-15,,180',
  'Jane,Doe,5559876543,jane@example.com,456 Oak Ave,Dallas,TX,75201,,Monthly Lawn,2026-10-01,,',
].join('\n');

const MAX_FILE_BYTES = 20 * 1024 * 1024;        // 20 MB (matches backend)
const PREVIEW_ROW_LIMIT = 5;
const POLL_INTERVAL_MS = 2000;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function parseCSVPreview(text) {
  const lines = text.trim().split('\n').filter(Boolean);
  if (lines.length < 2) return null;
  const splitLine = (line) =>
    line.split(',').map((cell) => cell.trim().replace(/^"|"$/g, ''));
  const headers = splitLine(lines[0]);
  const rows = lines.slice(1, 1 + PREVIEW_ROW_LIMIT).map(splitLine);
  return { headers, rows };
}

function downloadTemplate(mode) {
  const csv = mode === 'migration' ? TEMPLATE_CSV_MIGRATION : TEMPLATE_CSV_LEADS;
  const filename =
    mode === 'migration'
      ? 'cleanbid_migrate_customers_template.csv'
      : 'cleanbid_import_leads_template.csv';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * CsvImportModal — dual-mode component for prospect & customer imports.
 *
 * Wired to the DURABLE onboarding pipeline at
 *   POST /api/import/leads/durable
 * which returns a jobId immediately. The modal then polls
 *   GET  /api/import/jobs/{jobId}
 * every 2 s until the job reaches a terminal state
 * (completed | cancelled | failed).
 *
 * Legacy synchronous /api/import/leads is retained ONLY for rollback.
 */
const CsvImportModal = ({ isOpen, onClose, mode = 'leads' }) => {
  const isMigration = mode === 'migration';
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileError, setFileError] = useState('');
  const [job, setJob] = useState(null);              // durable job doc
  const [failedRows, setFailedRows] = useState([]);  // fetched on completion
  const [isUploading, setIsUploading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [conflictJobId, setConflictJobId] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setFileError('');
    setJob(null);
    setFailedRows([]);
    setIsUploading(false);
    setApiError('');
    setConflictJobId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e) => {
    setFileError('');
    setJob(null);
    setApiError('');
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!selected.name.toLowerCase().endsWith('.csv')) {
      setFileError('Please select a .csv file.');
      setFile(null); setPreview(null); return;
    }
    if (selected.size > MAX_FILE_BYTES) {
      setFileError('File exceeds the 20 MB limit.');
      setFile(null); setPreview(null); return;
    }

    setFile(selected);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(parseCSVPreview(ev.target?.result || ''));
    reader.readAsText(selected);
  };

  // ── Durable job polling ────────────────────────────────────────────
  const attachToJob = (jobId) => {
    // Kick immediate fetch then interval.
    const fetchOnce = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`${BACKEND_URL}/api/import/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setJob(data);
        if (['completed', 'cancelled', 'failed'].includes(data.status)) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          // On terminal state fetch the failed rows list (bounded 200).
          if ((data.failedRows || 0) > 0) {
            try {
              const rres = await fetch(
                `${BACKEND_URL}/api/import/jobs/${jobId}/rows?status=failed&limit=200`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              if (rres.ok) {
                const rdata = await rres.json();
                setFailedRows(rdata.items || []);
              }
            } catch (_) { /* non-fatal — job doc still available */ }
          }
        }
      } catch (_) { /* transient network — retry on next tick */ }
    };
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
  };

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const handleImport = async () => {
    if (!file) return;
    setApiError('');
    setIsUploading(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setApiError('Authentication required. Please reload and try again.');
        return;
      }
      const token = await currentUser.getIdToken(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${BACKEND_URL}/api/import/leads/durable`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.status === 409) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || {};
        setConflictJobId(detail.activeJobId || null);
        setApiError(detail.message || 'An import is already running for this workspace.');
        // Attach to the existing job so the operator can watch it to completion.
        if (detail.activeJobId) attachToJob(detail.activeJobId);
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setApiError(err.detail || `Import failed (HTTP ${res.status})`);
        return;
      }

      const data = await res.json();
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      attachToJob(data.jobId);
    } catch (err) {
      setApiError(err.message || 'Unexpected error. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelJob = async () => {
    if (!job?.id) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${BACKEND_URL}/api/import/jobs/${job.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) { /* poll loop will surface the terminal status */ }
  };

  if (!isOpen) return null;

  const jobStatus = job?.status;
  const isTerminal = ['completed', 'cancelled', 'failed'].includes(jobStatus || '');
  const isRunning = ['queued', 'processing'].includes(jobStatus || '');
  const progressPct = job && job.totalRows
    ? Math.min(100, Math.round(((job.processedRows || 0) / job.totalRows) * 100))
    : 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2
                className="text-2xl font-bold"
                style={{ color: 'var(--cb-navy)' }}
                data-testid="csv-import-modal-title"
              >
                {isMigration ? 'Migrate Customers from CSV' : 'Import Leads from CSV'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {isMigration
                  ? 'Bring your existing book of business in one upload · Up to 10,000 rows · 20 MB max'
                  : 'Up to 10,000 rows · 20 MB max · Duplicates skipped automatically'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              disabled={isUploading}
              data-testid="csv-import-close-btn"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 p-6 space-y-5">
            {!job && (
              <>
                {/* Step 1: Template */}
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Step 1 — Download the template</p>
                  {isMigration ? (
                    <div className="text-xs text-gray-500 mb-3 space-y-2">
                      <p>
                        <span className="font-semibold text-gray-700">Required for every row:</span>{' '}
                        <code className="bg-gray-100 px-1 rounded text-[11px]">
                          first_name, last_name, phone, email, address, city, state, zip
                        </code>
                      </p>
                      <p>
                        <span className="font-semibold text-indigo-700">Migration fields</span> (assign each customer to a Service Program):{' '}
                        <code className="bg-indigo-50 px-1 rounded text-[11px] text-indigo-900">
                          program_name, next_service_date, assigned_tech, price_override
                        </code>
                      </p>
                      <p className="text-[11px] text-gray-500 leading-relaxed pt-1">
                        <code className="px-1 bg-gray-100 rounded">program_name</code> must match an existing Service Program exactly
                        (configure in Settings → Service Programs). <code className="px-1 bg-gray-100 rounded">next_service_date</code>{' '}
                        is the customer&apos;s first scheduled visit (YYYY-MM-DD). The program owns interval, routing mode, season,
                        products, and visit sequence — you do not fill those in.{' '}
                        <span className="text-amber-700">Unmatched program names fail the row with a clear error.</span>
                      </p>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mb-3 space-y-2">
                      <p>Use this template to bring a list of prospects into your pipeline.</p>
                      <p>
                        <span className="font-semibold text-gray-700">Columns:</span>{' '}
                        <code className="bg-gray-100 px-1 rounded text-[11px]">
                          first_name, last_name, phone, email, address, city, state, zip, stage
                        </code>
                      </p>
                      <p className="text-[11px] text-gray-500 leading-relaxed pt-1">
                        Migrating existing recurring customers from another CRM? Use{' '}
                        <span className="font-semibold">Clients → Migrate Customers</span> instead — it lets you assign each
                        customer to a Service Program in the same upload.
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => downloadTemplate(mode)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                    data-testid="csv-template-download-btn"
                  >
                    <Download className="h-4 w-4" />
                    Download Template
                  </button>
                </div>

                {/* Step 2: File Select */}
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Step 2 — Select your CSV file</p>
                  <label
                    htmlFor="csv-file-input"
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-[var(--cb-orange)] transition-colors"
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {file ? (
                        <span className="font-medium text-[var(--cb-navy)]">{file.name}</span>
                      ) : (
                        'Click to select a .csv file'
                      )}
                    </span>
                    <input
                      id="csv-file-input"
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={isUploading}
                      data-testid="csv-file-input"
                    />
                  </label>
                  {fileError && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      {fileError}
                    </p>
                  )}
                </div>

                {/* Preview */}
                {preview && (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">
                      Preview{' '}
                      <span className="font-normal text-gray-400">
                        (first {Math.min(preview.rows.length, PREVIEW_ROW_LIMIT)} rows)
                      </span>
                    </p>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200">
                            {preview.headers.map((h) => (
                              <th key={h} className="py-1.5 pr-3 text-left font-semibold text-gray-600 whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.rows.map((row, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              {row.map((cell, j) => (
                                <td key={j} className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">
                                  {cell || <span className="text-gray-300">—</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* API error */}
            {apiError && (
              <Alert variant="destructive" data-testid="csv-import-api-error">
                <AlertDescription>
                  {apiError}
                  {conflictJobId && (
                    <p className="text-xs mt-1 opacity-80">Attached to active job {conflictJobId}.</p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Live progress */}
            {job && (
              <div
                className="rounded-lg border border-gray-200 p-4 space-y-4"
                data-testid="csv-import-job-panel"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">
                    {isRunning && 'Import in progress'}
                    {jobStatus === 'completed' && 'Import Complete'}
                    {jobStatus === 'cancelled' && 'Import Cancelled'}
                    {jobStatus === 'failed' && 'Import Failed'}
                  </p>
                  {isRunning && (
                    <button
                      onClick={handleCancelJob}
                      className="text-xs text-red-600 hover:underline"
                      data-testid="csv-import-cancel-btn"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {/* Progress bar */}
                <div className="space-y-1">
                  <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-[var(--cb-orange)] transition-all"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 tabular-nums">
                    {job.processedRows || 0} / {job.totalRows || 0} rows processed ({progressPct}%)
                  </p>
                </div>

                {/* Counter grid */}
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Total', value: job.totalRows || 0, color: 'text-gray-700' },
                    { label: 'Imported', value: job.importedRows || 0, color: 'text-green-600' },
                    { label: 'Skipped', value: job.skippedRows || 0, color: 'text-yellow-600' },
                    { label: 'Failed', value: job.failedRows || 0, color: 'text-red-600' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="text-center rounded-lg bg-gray-50 p-3">
                      <p className={`text-xl font-bold ${color}`} data-testid={`csv-import-count-${label.toLowerCase()}`}>{value}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>

                {isTerminal && (job.importedRows || 0) > 0 && (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
                    <CheckCircle className="h-4 w-4 flex-shrink-0" />
                    {job.importedRows} contact{job.importedRows !== 1 ? 's' : ''} added to your workspace.
                  </div>
                )}

                {/* Per-program summary */}
                {(job.activatedRecurring || 0) > 0 && job.programCounts && Object.keys(job.programCounts).length > 0 && (
                  <div
                    className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 space-y-2"
                    data-testid="csv-import-program-summary"
                  >
                    <div className="flex items-center gap-2 text-sm font-semibold text-indigo-800">
                      <Repeat className="h-4 w-4 flex-shrink-0" />
                      {job.activatedRecurring} recurring customer{job.activatedRecurring !== 1 ? 's' : ''} activated
                    </div>
                    <ul className="space-y-0.5 text-xs text-indigo-900 pl-6">
                      {Object.entries(job.programCounts)
                        .sort(([, a], [, b]) => b - a)
                        .map(([programName, count]) => (
                          <li key={programName} className="flex justify-between">
                            <span>{programName}</span>
                            <span className="font-medium tabular-nums">{count}</span>
                          </li>
                        ))}
                    </ul>
                    <p className="text-[11px] text-indigo-700 pl-6 leading-relaxed">
                      Visit #1 has been scheduled for each customer on the date you provided.
                    </p>
                  </div>
                )}

                {/* Failed rows */}
                {isTerminal && failedRows.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-700 mb-1.5 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" /> Failed rows (not imported)
                    </p>
                    <ul className="space-y-1 max-h-48 overflow-y-auto">
                      {failedRows.map((r) => (
                        <li key={r.id} className="text-xs text-gray-600 bg-red-50 rounded px-3 py-1.5">
                          Row {r.rowIndex} · {r.errorCode || 'error'}: {r.errorMessage}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Errors preview from job doc (visible during running too) */}
                {(job.errorsPreview || []).length > 0 && !isTerminal && (
                  <div>
                    <p className="text-xs font-semibold text-red-700 mb-1.5 flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" /> Recent row errors
                    </p>
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {(job.errorsPreview || []).slice(-10).map((e, i) => (
                        <li key={i} className="text-xs text-gray-600 bg-red-50 rounded px-3 py-1.5">
                          Row {e.row} · {e.code}: {e.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading}>
              {isTerminal ? 'Done' : 'Close'}
            </Button>

            {!job && (
              <Button
                type="button"
                onClick={handleImport}
                disabled={!file || isUploading}
                className="text-white"
                style={{ backgroundColor: 'var(--cb-orange)' }}
                data-testid="csv-import-submit-btn"
              >
                {isUploading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading…
                  </span>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CsvImportModal;
